import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Encapsulates the Expenses-app iframe handshake:
 *  - Tracks iframe load state.
 *  - Listens for the `EXPENSE_LOGGED` postMessage from the trusted Expenses
 *    origin, persists the created expense id back onto the bill, and fires the
 *    caller's `onLogged`/`onClose` callbacks.
 *
 * Behavior is identical to the previous inline effects inside ExpenseIframeModal:
 * strict origin validation, single-fire guard, and success/error toasts.
 *
 * @returns {{ iframeLoading: boolean, onIframeLoad: () => void }}
 */
export function useExpenseIframe({ open, link, bill, onClose, onLogged }) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const loggedRef = useRef(false);

  // Reset transient state whenever a new session opens.
  useEffect(() => {
    if (open) {
      setIframeLoading(true);
      loggedRef.current = false;
    }
  }, [open, link?.iframe_url]);

  // Listen for the save confirmation from the Expenses app.
  useEffect(() => {
    if (!open || !link) return undefined;
    const expectedOrigin = link.expenses_origin;

    const handler = async (event) => {
      // Strict origin validation — only trust the Expenses app.
      if (expectedOrigin && event.origin !== expectedOrigin) return;
      const data = event.data || {};
      if (data.type !== "EXPENSE_LOGGED") return;
      if (loggedRef.current) return;
      loggedRef.current = true;

      try {
        await api.expenseLogged(bill.id, {
          expense_id: data.expense_id || null,
          status: data.status || "logged",
        });
        toast.success("Expense logged", {
          description: data.expense_id ? `Linked as ${data.expense_id}` : undefined,
        });
        onLogged && onLogged();
        onClose && onClose();
      } catch {
        toast.error("Saved in Expenses, but linking back to this bill failed.");
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [open, link, bill, onClose, onLogged]);

  return { iframeLoading, onIframeLoad: () => setIframeLoading(false) };
}
